import React from "react";
import moment from "moment";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import Input from "../../../component-lib/Atoms/Input/Input";
import Button from "../../../component-lib/Molecules/button/Button";
import {
  COMMON_COLOR565656,
  COMMON_FONT16_500,
  COMMON_MARGIN_BOTTOM_8PX,
} from "./constant";

interface IntervalNotesSectionProps {
  // Already-filed interval notes (read-only): [{ id, note, createdAt, createdBy }]
  intervalNotes: any[];
  // Note is finalized/discharged -> hide the input entirely.
  isReadOnly: boolean;
  noteText: string;
  onNoteTextChange: (value: string) => void;
  // Whether the new empty field is currently shown (after "Add More").
  showNewField: boolean;
  onAddMore: () => void;
}

// Shared "Interval Notes" section used by the offsite (Listening Ear) flow and
// the Physician flow. Filed notes render read-only; one empty field shows the
// first time, "Add More" reveals the next field (and hides itself). The footer
// "File Interval Note" button (owned by each parent flow) commits the typed note
// — which creates the visit note if one doesn't exist yet, so notes are addable
// any time.
export default function IntervalNotesSection({
  intervalNotes,
  isReadOnly,
  noteText,
  onNoteTextChange,
  showNewField,
  onAddMore,
}: IntervalNotesSectionProps) {
  const hasFiled = (intervalNotes?.length || 0) > 0;
  const showField = !hasFiled || showNewField;

  return (
    <div className="flex flex-col pt-5">
      <Heading
        title="Interval Notes (optional)"
        customStyle={{
          ...COMMON_FONT16_500,
          ...COMMON_COLOR565656,
          ...COMMON_MARGIN_BOTTOM_8PX,
        }}
      />
      {/* Filed interval notes (read-only) */}
      {hasFiled && (
        <div className="flex flex-col gap-2 mb-3">
          {intervalNotes.map((item: any, i: number) => (
            <div
              key={item?.id || i}
              className="border-radius-8px"
              style={{
                padding: "12px 16px",
                border: "1px solid #D2D2D2",
                background: "#F8F8F8",
              }}
            >
              <div className="font-size-12px color-979797 mb-1">
                {item?.createdAt
                  ? moment(item?.createdAt).format("MM/DD/YYYY hh:mm A")
                  : ""}
                {item?.createdBy
                  ? ` · ${item?.createdBy?.firstName || ""} ${
                      item?.createdBy?.lastName || ""
                    }`
                  : ""}
              </div>
              <div className="font-weight-500 color-565656">{item?.note}</div>
            </div>
          ))}
        </div>
      )}
      {/* Add a new interval note (addable any time — filing creates the note if
          one doesn't exist yet). One empty field shows the first time; "Add
          More" reveals the next field. */}
      {!isReadOnly &&
        (showField ? (
          <Input
            rows={3}
            size="small"
            placeholder="Enter Interval Note"
            multiline={true}
            maxWords={250}
            name="intervalNote"
            value={noteText}
            onChangeCb={(e: any) => {
              onNoteTextChange(e?.target?.value);
            }}
          />
        ) : (
          <div className="flex justify-end">
            <Button
              label="Add More"
              variant="contained"
              onClickCb={onAddMore}
              style={{
                background: "#E4F3FF",
                color: "#005596",
                padding: "7px 16px",
              }}
            />
          </div>
        ))}
    </div>
  );
}
